import { ArrowRight } from "lucide-react";
import { differenceInDays } from "date-fns";
import { useCurrency } from "@/hooks/useUserCurrency";

interface Loan {
  id: string;
  clientId: string;
  clientName: string;
  amount: number;
  dueDate: Date;
  status: "paid" | "active" | "overdue";
  installments?: number;
  paidInstallments?: number;
}

interface Props {
  loans: Loan[];
  onViewAll?: () => void;
}

const avatarClasses = ["avatar-grad-1", "avatar-grad-2", "avatar-grad-3", "avatar-grad-4", "avatar-grad-5"];

function initials(name: string) {
  return name
    .split(" ")
    .filter(Boolean)
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function avatarFor(id: string) {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return avatarClasses[Math.abs(h) % avatarClasses.length];
}

export function UpcomingDuesPanel({ loans, onViewAll }: Props) {
  const { format } = useCurrency();
  const today = new Date();
  const upcoming = loans
    .filter((l) => l.status === "active" || l.status === "overdue")
    .map((l) => ({ ...l, days: differenceInDays(l.dueDate, today) }))
    .sort((a, b) => a.days - b.days)
    .slice(0, 12);

  const top = upcoming.slice(0, 3);
  const rest = upcoming.length - top.length;

  return (
    <div className="surface-card p-5 lg:p-6 h-full flex flex-col">
      <h3 className="text-base lg:text-[17px] font-semibold tracking-tight text-foreground mb-4">
        Vencimentos próximos
      </h3>

      {top.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nenhum vencimento próximo.</p>
      ) : (
        <div className="space-y-3 flex-1">
          {top.map((loan) => {
            const overdue = loan.days < 0;
            const due = loan.days === 0;
            const installmentLabel =
              loan.installments && loan.installments > 1
                ? `${(loan.paidInstallments || 0) + 1}ª parcela`
                : "Parcela única";
            const badge =
              overdue
                ? { txt: `${Math.abs(loan.days)} dia${Math.abs(loan.days) === 1 ? "" : "s"} atrás`, cls: "bg-[hsl(4_70%_59%/0.13)] text-[hsl(4_70%_45%)]" }
                : due
                ? { txt: "hoje", cls: "bg-[hsl(28_92%_44%/0.16)] text-[hsl(28_92%_38%)]" }
                : loan.days <= 3
                ? { txt: `${loan.days} dia${loan.days === 1 ? "" : "s"}`, cls: "bg-[hsl(28_92%_44%/0.16)] text-[hsl(28_92%_38%)]" }
                : { txt: `${loan.days} dias`, cls: "bg-[hsl(225_75%_53%/0.12)] text-[hsl(225_75%_45%)]" };

            return (
              <div key={loan.id} className="flex items-center gap-3 p-2 -mx-2 rounded-xl row-hover">
                <div className={`h-10 w-10 rounded-full ${avatarFor(loan.clientId)} flex items-center justify-center text-[12px] font-bold`}>
                  {initials(loan.clientName)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[13.5px] font-semibold text-foreground truncate">{loan.clientName}</div>
                  <div className="text-[11.5px] text-muted-foreground truncate">{installmentLabel}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[13px] font-bold tabular text-foreground">
                    {format(loan.amount, { maximumFractionDigits: 0 })}
                  </div>
                  <span className={`mt-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10.5px] font-semibold ${badge.cls}`}>
                    {badge.txt}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {rest > 0 && (
        <button
          onClick={onViewAll}
          className="mt-4 inline-flex items-center gap-1.5 text-[12.5px] font-medium text-[hsl(225_75%_53%)] hover:text-[hsl(228_74%_45%)] transition-colors self-center"
        >
          +{rest} outros vencimentos <ArrowRight className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}